import { z } from "zod";
import { optionalIco } from "../lib/zod-helpers";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — lead magnet diagnóza (plán §A, §S.2.5).
 *
 * Veřejný návštěvník vyplní sedmioblastní score, tRPC spočítá totalScore
 * (průměr) a doporučí produkt podle nejslabší oblasti.
 */

const uuidMsg = "Neplatné UUID";
const scoreField = z
  .number()
  .int("Skóre musí být celé číslo")
  .min(0, "Skóre musí být alespoň 0")
  .max(100, "Skóre musí být nejvíc 100");

export const diagnozaScoresSchema = z.object({
  komunikace: scoreField,
  obchod: scoreField,
  leadership: scoreField,
  management: scoreField,
  operativa: scoreField,
  strategie: scoreField,
  finance: scoreField,
});

export type DiagnozaScoresInput = z.infer<typeof diagnozaScoresSchema>;

/** Public submit — rate-limited v routeru. */
export const submitDiagnozaSchema = z.object({
  email: z.preprocess(
    (val) => (typeof val === "string" ? val.trim().toLowerCase() : val),
    z.string().email("Neplatný e-mail").max(255, "E-mail musí být 255 znaků nebo méně"),
  ),
  jmeno: z.string().max(255, "Jméno musí být 255 znaků nebo méně").optional(),
  telefon: z
    .string()
    .trim()
    .min(1, "Telefon je povinný")
    .max(32, "Telefon musí být 32 znaků nebo méně")
    .refine((v) => /^[+\d(][\d\s\-()/]*$/.test(v), "Telefon obsahuje neplatné znaky")
    .refine((v) => (v.match(/\d/g)?.length ?? 0) >= 6, "Telefon musí mít alespoň 6 číslic"),
  ico: optionalIco(),
  firma: z.string().max(255, "Firma musí být 255 znaků nebo méně").optional(),
  scores: diagnozaScoresSchema,
  consent: z.literal(true),
  /** Cloudflare Turnstile token (server ověří, je-li nakonfigurováno). */
  turnstileToken: z.string().max(4096, "Neplatný token ověření").optional(),
});

/** Admin list s filtry + paginace. */
export const listDiagnozySchema = paginationSchema.extend({
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  unconvertedOnly: z.coerce.boolean().optional(),
});

export const diagnozaIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Admin převod diagnózy na klienta. Volitelně přiřadí obchodníka. */
export const convertToKlientSchema = z.object({
  id: z.string().uuid(uuidMsg),
  obchodnikId: z.string().max(255, "ID obchodníka musí být 255 znaků nebo méně").optional(),
});

export type SubmitDiagnozaInput = z.infer<typeof submitDiagnozaSchema>;
export type ListDiagnozyInput = z.infer<typeof listDiagnozySchema>;
export type DiagnozaIdInput = z.infer<typeof diagnozaIdSchema>;
export type ConvertToKlientInput = z.infer<typeof convertToKlientSchema>;
